import { useId, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react'
import './chartPaneLayout.css'

interface ChartPaneFrameProps {
  id: string
  label: string
  width: number
  onWidthChange: (width: number) => void
  dropProps: HTMLAttributes<HTMLDivElement>
  orderControls: ReactNode
  children: ReactNode
}

/** Grid placement belongs to a pane, independently of its exchange or metric. */
export function ChartPaneFrame({ id, label, width, onWidthChange, dropProps, orderControls, children }: ChartPaneFrameProps) {
  return (
    <div {...dropProps}
      className={`screener-chart-pane min-w-0 ${dropProps.className || ''}`}
      style={{ '--chart-columns': width } as CSSProperties}
      data-chart-pane={id}
      data-exchange-chart={id.startsWith('oi:') ? id.slice(3) : undefined}
      data-cvd-chart={id.startsWith('cvd:') ? id.slice(4) : undefined}>
      <div className="mb-1 flex min-h-6 flex-wrap items-center justify-between gap-1 px-1">
        {orderControls}
        <ChartWidthControl key={width} label={label} width={width} onChange={onWidthChange} />
      </div>
      {children}
    </div>
  )
}

function ChartWidthControl({ label, width, onChange }: { label: string; width: number; onChange: (width: number) => void }) {
  const presetsId = useId()
  const [draft, setDraft] = useState(String(width))
  const commit = () => {
    const parsed = Number(draft)
    const next = draft.trim() && Number.isFinite(parsed) ? Math.min(100, Math.max(1, Math.round(parsed))) : width
    setDraft(String(next))
    if (next !== width) onChange(next)
  }
  return (
    <label className="flex min-w-0 items-center gap-1 text-[10px] text-slate-500">
      Width
      <input aria-label={`Width of ${label}`} type="number" min={1} max={100} step={1}
        list={presetsId} value={draft} onChange={(event) => setDraft(event.target.value)}
        onBlur={commit} onFocus={(event) => event.target.select()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur() }
          if (event.key === 'Escape') { event.preventDefault(); setDraft(String(width)) }
        }}
        title="1–100% of the row · Enter or leave the field to apply · Charts stack in narrow workspaces"
        className="w-14 min-w-0 rounded border border-slate-700/70 bg-slate-950 px-1 py-0.5 text-[10px] text-slate-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300" />
      %
      <datalist id={presetsId}>
        {[25, 50, 75, 100].map((value) => <option key={value} value={value} />)}
      </datalist>
    </label>
  )
}
